<script lang="ts">
	import { _ } from "svelte-i18n";
	import { Modal, ModalBody, ModalHeader } from "@sveltestrap/sveltestrap";
	import type { DropdownOption, V2Persona } from "../../../common/types";
	import CustomPersonaForm from "./CustomPersonaForm.svelte";
	import { CUSTOM_PERSONA_ID, type DraftCustomPersona } from "./customPersonaTypes.ts";

	let {
		isOpen = $bindable(false),
		personas,
		selectedId,
		customPersona = null,
		availableVoices = [],
		onSelect,
		onSelectCustom,
		onDiscardCustom,
	}: {
		isOpen?: boolean;
		personas: V2Persona[];
		selectedId: string;
		customPersona?: DraftCustomPersona | null;
		availableVoices?: DropdownOption[];
		onSelect?: (id: string) => void;
		onSelectCustom?: (draft: DraftCustomPersona) => void;
		onDiscardCustom?: () => void;
	} = $props();

	type View = "grid" | "form";
	let view = $state<View>("grid");

	function close() {
		isOpen = false;
		view = "grid";
	}

	function pickPreset(id: string) {
		onSelect?.(id);
	}

	function pickCustom() {
		if (customPersona) {
			onSelectCustom?.(customPersona);
		}
	}

	function openForm() {
		view = "form";
	}

	function onFormSave(draft: DraftCustomPersona) {
		onSelectCustom?.(draft);
		view = "grid";
	}

	function onFormCancel() {
		view = "grid";
	}

	function discardCustom(event: MouseEvent) {
		event.stopPropagation();
		onDiscardCustom?.();
	}
</script>

<Modal {isOpen} toggle={close} size="lg">
	<ModalHeader toggle={close}>
		{$_("landingpage.newPresentation.personaModalTitle")}
	</ModalHeader>
	<ModalBody>
		{#if view === "grid"}
			<div class="persona-grid">
				{#each personas as persona (persona.id)}
					<button
						type="button"
						class="persona-card"
						class:selected={persona.id === selectedId}
						onclick={() => pickPreset(persona.id)}
					>
						<img src={persona.imageUrl} alt={persona.label} />
						<div class="persona-text">
							<div class="persona-label">{persona.label}</div>
							<div class="persona-name">{persona.name}</div>
							<div class="persona-personality">{persona.personality}</div>
						</div>
					</button>
				{/each}

				{#if customPersona}
					<div
						class="persona-card persona-card-custom"
						class:selected={selectedId === CUSTOM_PERSONA_ID}
					>
						<button type="button" class="persona-card-button" onclick={pickCustom}>
							{#if customPersona.imageBase64}
								<img src={customPersona.imageBase64} alt={customPersona.label} />
							{:else}
								<div class="persona-placeholder">
									<span aria-hidden="true">👤</span>
								</div>
							{/if}
							<div class="persona-text">
								<div class="persona-label">{customPersona.label}</div>
								<div class="persona-name">{customPersona.name}</div>
								<div class="persona-personality">{customPersona.personality}</div>
							</div>
						</button>
						<button
							type="button"
							class="discard-btn"
							aria-label={$_("landingpage.newPresentation.customPersona.discardCustom")}
							onclick={discardCustom}
						>
							×
						</button>
					</div>
				{/if}

				<button type="button" class="persona-card persona-card-create" onclick={openForm}>
					<div class="persona-placeholder">
						<span aria-hidden="true">+</span>
					</div>
					<div class="persona-text">
						<div class="persona-label">
							{$_("landingpage.newPresentation.customPersona.createTile")}
						</div>
					</div>
				</button>
			</div>
		{:else}
			<CustomPersonaForm
				initial={customPersona}
				{availableVoices}
				onSave={onFormSave}
				onCancel={onFormCancel}
			/>
		{/if}
	</ModalBody>
</Modal>

<style>
	.persona-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
		gap: 1rem;
	}
	.persona-card {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		gap: 0.5rem;
		padding: 0.75rem;
		border: 2px solid transparent;
		border-radius: 0.5rem;
		background: var(--bs-body-bg, #fff);
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		cursor: pointer;
		text-align: left;
		position: relative;
		transition:
			border-color 120ms ease,
			box-shadow 120ms ease;
	}
	.persona-card:hover {
		border-color: var(--bs-primary, #0d6efd);
	}
	.persona-card.selected {
		border-color: var(--bs-primary, #0d6efd);
		box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
	}
	.persona-card img {
		width: 100%;
		aspect-ratio: 1 / 1;
		object-fit: cover;
		border-radius: 0.375rem;
		background: #eee;
	}
	.persona-card-button {
		all: unset;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		cursor: pointer;
		width: 100%;
	}
	.persona-placeholder {
		width: 100%;
		aspect-ratio: 1 / 1;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 3rem;
		color: var(--bs-secondary, #6c757d);
		background: #f1f3f5;
		border-radius: 0.375rem;
	}
	.persona-label {
		font-weight: 600;
	}
	.persona-name {
		color: var(--bs-secondary, #6c757d);
		font-size: 0.875rem;
	}
	.persona-personality {
		margin-top: 0.25rem;
		font-size: 0.8125rem;
		color: var(--bs-secondary, #6c757d);
		display: -webkit-box;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
	.persona-card-create {
		align-items: center;
		justify-content: center;
		border-style: dashed;
		border-color: var(--bs-border-color, #dee2e6);
		color: var(--bs-secondary, #6c757d);
	}
	.discard-btn {
		position: absolute;
		top: 0.25rem;
		right: 0.25rem;
		width: 1.5rem;
		height: 1.5rem;
		border-radius: 50%;
		border: none;
		background: rgba(0, 0, 0, 0.55);
		color: #fff;
		font-size: 1rem;
		line-height: 1;
		cursor: pointer;
	}
	.discard-btn:hover {
		background: rgba(0, 0, 0, 0.75);
	}
</style>
